import React from 'react'

import { Dashboard, PageLayout } from '@components/common'
import { NavTabs } from '@components/common/NavTabs'
import {
  BaseContent,
  ButtonClean,
  EmptyState,
  HeadContent,
  Loading,
  Pagination,
  useModalContext,
} from '@fourthwall/components'
import { routing } from '@utils/routing'

import * as S from './ThankYous.styled'
import type { State, ThankYou } from './ThankYous.types'
import { NoThankYousContent } from './components/NoThankYousContent/NoThankYousContent'
import { ThankYousData } from './components/ThankYousData/ThankYousData'
import { useThankYous } from './hooks/useThankYous'
import { SendThankYouModal } from './modules/SendThankYouModal'
import { ThankYousBar } from './modules/ThankYousBar'

const emptyStateMessages: Record<State, { title: string; subtitle?: string }> = {
  WAITING: {
    title: "You're all caught up! 🤗",
    subtitle: 'Thanks for showing your fans the love they deserve.',
  },
  SKIPPED: {
    title: "You don't have any skipped thank yous 😇",
  },
  SENT: {
    title: "You haven't sent any thank yous yet,",
  },
}

const tabs = [
  { label: 'Remaining', value: 'WAITING' as const },
  { label: 'Skipped', value: 'SKIPPED' as const },
  { label: 'Sent', value: 'SENT' as const },
]

export const ThankYous = () => {
  const { thankYous } = useThankYous()
  const { open } = useModalContext()

  const openThankYouModal = (item: ThankYou) => {
    open(SendThankYouModal, {
      thankYous: [item],
      onSuccess: thankYous.refetch,
    })
  }

  const renderData = (emptyState: boolean) => (
    <Dashboard.Section.Item>
      <ThankYousData
        data={thankYous.data}
        isLoading={thankYous.isLoading}
        activeTab={thankYous.activeTab}
        emptyComponent={
          emptyState ? (
            <EmptyState
              title={emptyStateMessages[thankYous.activeTab].title}
              subtitle={emptyStateMessages[thankYous.activeTab].subtitle}
            />
          ) : undefined
        }
        onRowClick={openThankYouModal}
      />
    </Dashboard.Section.Item>
  )

  const renderThankYousContent = () => (
    <>
      <Dashboard.Section.Item>
        <NavTabs tabs={tabs} activeTab={thankYous.activeTab} onTabClick={thankYous.setState} />
      </Dashboard.Section.Item>
      {renderData(true)}
      {!!thankYous.totalPages && (
        <Dashboard.Section.Item>
          <Pagination
            currentPage={thankYous.currentPage}
            pageCount={thankYous.totalPages}
            onChange={thankYous.setPage}
          />
        </Dashboard.Section.Item>
      )}
    </>
  )

  const renderNoThankYousContent = () => (
    <>
      {renderData(false)}
      <Loading isLoading={thankYous.isLoading} variant="solid">
        {!thankYous.isLoading && <NoThankYousContent />}
      </Loading>
    </>
  )

  const renderBottomContent = () => {
    if (thankYous.noThankYous) return null

    return (
      <S.BottomContentContainer>
        <BaseContent>
          Thank your top supporters with a quick video, audio, or picture message! Thank yous take
          less than 15 seconds to send and recipients are 5x more likely to place another order.{' '}
          <ButtonClean
            label="Learn more"
            href={routing.external.helpCenter.thankYous}
            target="_blank"
          />
        </BaseContent>
        <ThankYousBar type="MODAL" onModalSuccess={thankYous.refetch} />
      </S.BottomContentContainer>
    )
  }

  return (
    <>
      <HeadContent title="Thank yous" />
      <PageLayout size="large">
        <Dashboard.Section
          name="Thank yous"
          bottomContent={renderBottomContent()}
          bottomMargin="medium"
        >
          {thankYous.noThankYous ? renderNoThankYousContent() : renderThankYousContent()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
